// University of San Francisco: public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - USF's Common Data Set 2024-2025, linked from the Center for
//    Institutional Planning and Effectiveness' Common Data Sets page on
//    myUSF. Departure: it is the newest edition USF posts (the page says
//    reports are published about a year after the period they cover), so the
//    counts are for the Fall 2024 class. C1 applicants, admits and enrollees
//    (all gender lines; they match the residency table's totals), C21 early
//    decision counts, C22 early action. The C1 residency table gives in-state
//    and total columns only, so no residency rows are shown. The edition's
//    C8 answers for Fall 2026 and its G1 costs are for 2025-2026, so neither
//    is used.
//  - USF Undergraduate Admission: First-Year Students, Dates and Deadlines,
//    Early Decision / Early Action / Regular Decision, What We Look For (test
//    optional) and First-Year Nursing Admission. The pages name no entry
//    year; they are the pages posted for the application open now (fall 2027
//    entry). Dates and Deadlines gives a Regular Decision notification of
//    mid-March; the ED/EA/RD page says early March. The dates page is used.
//    The testing line joins What We Look For with the First-Year page's
//    sentence on superscoring and the SAT writing section.
//  - USF Financial Aid, Undergraduate Student Tuition and Costs: the
//    2026-27 cost of attendance for a student living on campus, item by item,
//    with USF's own total.
//  - College Scorecard API, school id 122612 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'USF Common Data Set 2024-2025',
        url: 'https://myusf.usfca.edu/cipe/cds',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'USF Admission, First-Year Students',
        url: 'https://www.usfca.edu/admission/undergraduate/first-year',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'USF Admission, Dates and Deadlines',
        url: 'https://www.usfca.edu/admission/undergraduate/dates-deadlines',
        read: '2026-10-01',
    },
    plans: {
        label: 'USF Admission, Early Decision, Early Action, Regular Decision',
        url: 'https://www.usfca.edu/admission/undergraduate/early-decision-early-action-regular-decision',
        read: '2026-10-01',
    },
    testing: {
        label: 'USF Admission, What We Look For (Test Optional)',
        url: 'https://www.usfca.edu/admission/undergraduate/what-we-look-for',
        read: '2026-10-01',
    },
    nursing: {
        label: 'USF Admission, First-Year Nursing Admission',
        url: 'https://www.usfca.edu/admission/undergraduate/first-year-nursing',
        read: '2026-10-01',
    },
    costs: {
        label: 'USF Financial Aid, Undergraduate Student Tuition and Costs',
        url: 'https://www.usfca.edu/financial-aid/undergraduate-student-costs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?122612-University-of-San-Francisco',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-san-francisco',
    kbId: 'university_of_san_francisco',
    name: 'University of San Francisco',
    shortName: 'USF',
    aliases: ['USF'],
    location: 'San Francisco, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (USF graphics resources); decoration only.
    branding: {
        primary: '#00543C',
        secondary: '#FDBB30',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_san_francisco_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 24888,
        admitted: 15358,
        enrolled: 920,
        source: 'cds',
        where: 'section C1 (the 2024-2025 edition is the newest USF posts)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: true,
        year: 'Fall 2026',
        note: 'USF publishes first-year admission figures for one major, Nursing, which admits directly. The rate here is worked out from USF\'s counts; USF rounds it to 13%. USF reports 20% for fall 2024 and 16% for fall 2025.',
        head: ['Major', 'Applied', 'Offered admission', 'Rate'],
        rows: [
            { name: 'Nursing (BSN)', applied: 2925, admitted: 368, rate: 12.6 },
        ],
        source: 'nursing',
        where: 'First-Year Nursing Admission Statistics',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        year: 'Fall 2024',
        rows: [
            { plan: 'Early decision', applied: 85, admitted: 42 },
        ],
        note: 'The Common Data Set gives early decision counts for the fall 2024 class and no early action counts, so there is no early action rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to USF on the Common App, the Coalition App or USF\'s own application (not for nursing); all are treated equally. One program asks for more:',
        rows: [
            { school: 'Nursing (BSN)', requirement: 'Direct entry: you are admitted to the nursing program when you are admitted to USF, and you cannot transfer into it later from another major. You must have completed chemistry, and biology or physics, before applying, and answer a nursing short answer question in the USF supplement. For Early Action and Early Decision I the deadline is November 2. USF reviews all nursing applications at the deadline, so applying early brings no priority.', source: 'nursing' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Mid-March', binding: false },
        ],
        note: 'Transcripts and other materials are due November 5 for Early Decision I and Early Action, January 29 for Early Decision II, and February 5 for Regular Decision. The deposit is due March 1 for both Early Decision rounds and May 1 for Early Action and Regular Decision.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding: if admitted, you withdraw all other college applications and attend USF. You can ask to be released only if your financial aid offer does not meet enough of your demonstrated financial need.',
        bindingSource: 'plans',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'USF does not require the SAT or ACT. On the application you choose whether your scores are considered; if you leave them out, the admission team does not see them, even if they were sent, and you are still considered for merit scholarships. USF superscores the SAT and the ACT and does not use the SAT writing section.',
        source: 'testing',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 63880 },
            { item: 'Fees', amount: 612 },
            { item: 'Housing', amount: 12930 },
            { item: 'Food', amount: 6220 },
            { item: 'Books, course materials, supplies and equipment', amount: 1306 },
            { item: 'Transportation', amount: 1076 },
            { item: 'Personal expenses', amount: 3274 },
        ],
        total: 89298,
        note: 'For a full-time undergraduate living on campus, fall and spring. The fees are the $302 student activity fee and the $310 transportation fee (a Muni pass).',
        source: 'costs',
        netPrice: {
            average: 30496,
            byIncome: [
                { income: '$0 to $30,000', amount: 23963 },
                { income: '$30,001 to $48,000', amount: 26079 },
                { income: '$48,001 to $75,000', amount: 25957 },
                { income: '$75,001 to $110,000', amount: 27201 },
                { income: 'Over $110,000', amount: 38553 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from USF\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
